/**
 * UserManagement Component
 * Read-only view of admin users. Renders as a page view (not a modal).
 * All access is managed via Azure Static Web Apps role assignments.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Users, RefreshCw, AlertCircle, ShieldCheck, Shield, Info } from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import '../styles/design-system.css';

const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const swaPrincipal = localStorage.getItem('swaPrincipal');
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
    };
};

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadUsers = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/users`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error('Failed to load users');
            }

            const data = await response.json();
            setUsers(data.users);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div>
            {/* Page Header */}
            <div className="ds-page-header">
                <div className="ds-page-header-top">
                    <div>
                        <h1 className="ds-page-title">Settings</h1>
                        <p className="ds-page-subtitle">Manage admin users and application settings.</p>
                    </div>
                    <button className="ds-btn ds-btn-secondary" onClick={loadUsers} disabled={isLoading}>
                        <RefreshCw size={16} className={isLoading ? 'spin' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            <div className="ds-page-body">
                {/* Info banner */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-4) var(--space-5)', background: 'var(--color-info-light)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-6)', fontSize: 'var(--font-size-sm)', color: '#4644AB' }}>
                    <Info size={16} style={{ flexShrink: 0 }} />
                    <span>User access is managed via Azure Static Web Apps role assignments. Invite users with the <strong>admin</strong> role. This list is read-only.</span>
                </div>

                {/* Error */}
                {error && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-error-light)', color: '#CC2F26', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
                        <AlertCircle size={16} />
                        <span style={{ flex: 1 }}>{error}</span>
                        <button className="ds-btn ds-btn-ghost" onClick={() => setError(null)} style={{ padding: '2px 6px' }}>Dismiss</button>
                    </div>
                )}

                {/* Loading */}
                {isLoading && !users.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-12)', color: 'var(--color-text-tertiary)' }}>
                        <RefreshCw size={24} className="spin" />
                        <p style={{ marginTop: 'var(--space-3)' }}>Loading users...</p>
                    </div>
                ) : (
                    /* Users table */
                    <div className="ds-card">
                        <div className="ds-card-header">
                            <div className="ds-card-header-left">
                                <div className="ds-card-icon">
                                    <Users size={18} />
                                </div>
                                <div>
                                    <div className="ds-card-title">Admin Users</div>
                                    <div className="ds-card-desc">{users.length} user{users.length !== 1 ? 's' : ''}</div>
                                </div>
                            </div>
                        </div>
                        {users.length === 0 ? (
                            <div style={{ padding: 'var(--space-12)', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                                <Users size={40} style={{ opacity: 0.3, marginBottom: 'var(--space-3)' }} />
                                <p>No users found.</p>
                            </div>
                        ) : (
                            <table className="ds-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Last Login</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(user => (
                                        <tr key={user.id} style={!user.is_active ? { opacity: 0.5 } : undefined}>
                                            <td>
                                                <div className="ds-cell-with-avatar">
                                                    <div className="ds-avatar">{(user.username || '?').charAt(0).toUpperCase()}</div>
                                                    <div>
                                                        <div className="ds-cell-name">{user.username}</div>
                                                        {user.full_name && <div className="ds-cell-email">{user.full_name}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{user.email}</td>
                                            <td>
                                                <span className={`ds-badge ${user.is_primary_admin ? 'ds-badge-info' : 'ds-badge-neutral'}`}>
                                                    {user.is_primary_admin ? <ShieldCheck size={12} /> : <Shield size={12} />}
                                                    {user.is_primary_admin ? 'Primary Admin' : 'Admin'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`ds-badge ${user.is_active ? 'ds-badge-success' : 'ds-badge-neutral'}`}>
                                                    <span className="ds-badge-dot" />
                                                    {user.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)' }}>{formatDate(user.last_login)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default UserManagement;
